import { create } from "zustand";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** Shared with the pre-paint script in index.html — both must read the same key. */
export const THEME_STORAGE_KEY = "smsbridge-theme";

const media = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: light)")
    : null;

function systemTheme(): ResolvedTheme {
  return media()?.matches ? "light" : "dark";
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? systemTheme() : preference;
}

function readStored(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // Private mode / storage disabled. "system" is the correct fallback anyway.
  }
  return "system";
}

/**
 * Writes the resolved theme to the <html> element.
 *
 * ALWAYS writes an explicit "light" or "dark" — never removes the attribute, even for the
 * "system" preference. That matters because ui-tokens' stylesheet contains
 * `@media (prefers-color-scheme: light) { :root:not([data-theme="dark"]) { ...light... } }`,
 * which has higher specificity than the base `:root` dark block. With no attribute set, a user
 * whose OS is light silently got the light palette regardless of preference — which is exactly
 * how this app was already leaking into an untested light mode before any switcher existed.
 * Writing the attribute unconditionally makes that media query inert and puts the resolved
 * value in sole control.
 */
function apply(resolved: ResolvedTheme): void {
  document.documentElement.setAttribute("data-theme", resolved);
  // Keeps native form controls, scrollbars and focus rings in step with the app.
  document.documentElement.style.colorScheme = resolved;
}

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Subscribes to OS changes; returns an unsubscribe. Called once from ThemeProvider. */
  init: () => () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: readStored(),
  resolved: resolveTheme(readStored()),

  setPreference: (preference) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // Non-fatal: the choice just won't survive a reload.
    }
    const resolved = resolveTheme(preference);
    apply(resolved);
    set({ preference, resolved });
  },

  init: () => {
    apply(get().resolved);

    const mq = media();
    if (!mq) return () => undefined;

    // Only meaningful while preference is "system"; re-reading it inside the handler means a
    // user switching OS appearance mid-session is followed live, without a stale closure.
    const onChange = () => {
      if (get().preference !== "system") return;
      const resolved = systemTheme();
      apply(resolved);
      set({ resolved });
    };

    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  },
}));
